import {usePlantStatus} from '@domain/kpis'
import {DataContentWrapper} from '@hconnect/common/containers'
import type {Iso8601} from '@hconnect/common/types'
import {useMemo, FC} from 'react'

import {PlantKpiData} from './PlantKpiData'

type PlantActualVsPlannedProps = {
  plantId: string
  from: Iso8601
  to: Iso8601
  quickSelectionId?: string | null
}

export const PlantActualVsPlanned: FC<PlantActualVsPlannedProps> = ({
  plantId,
  from,
  to,
  quickSelectionId
}) => {
  const {
    data: plantData,
    isError: isPlantStatusError,
    isLoading: isPlantStatusInitialLoading,
    isRefetching: isPlantStatusRefetching,
    refetch: refetchPlantStatus
  } = usePlantStatus(plantId, {
    startDate: from,
    endDate: to
  })
  const isGrindingUnit = plantData?.plant?.pxTrendMetaData?.isGrindingUnit

  const showKPIsAcVsPl =
    quickSelectionId &&
    [
      'lastYear',
      'yearToDateYTD',
      'last3months',
      'yearToMonthYTM',
      'currentMonth',
      'lastMonth'
    ].includes(quickSelectionId)

  const clinkerProductionData = useMemo(() => {
    return {
      unit: plantData?.unitClinker ?? '',
      planned: plantData?.plannedClinkerProduction ?? 0,
      actual: plantData?.actualClinkerProduction,
      delta: plantData?.clinkerDelta
    }
  }, [plantData])
  const cementProductionData = useMemo(() => {
    return {
      unit: plantData?.unitCement ?? '',
      planned: plantData?.plannedCementProduction ?? 0,
      actual: plantData?.actualCementProduction,
      delta: plantData?.cementDelta
    }
  }, [plantData])

  const productionData = useMemo(() => {
    return {
      cementProduction: {data: cementProductionData, isError: isPlantStatusError},
      clinkerProduction: {data: clinkerProductionData, isError: isPlantStatusError}
    }
  }, [cementProductionData, clinkerProductionData, isPlantStatusError])

  return (
    <DataContentWrapper
      data={plantData}
      showLoader={isPlantStatusInitialLoading}
      isError={false} // we show errors in the grids
      isRefetching={isPlantStatusRefetching}
      retryFunction={refetchPlantStatus}
    >
      <PlantKpiData
        showKPIs={showKPIsAcVsPl}
        productionData={productionData}
        plantId={plantId}
        isGrindingUnit={isGrindingUnit}
      />
    </DataContentWrapper>
  )
}
